import { type App, type Ref, nextTick } from 'vue'
import { type I18n, createI18n } from 'vue-i18n'
import type { Composer } from 'vue-i18n'
import type { Router } from 'vue-router'

declare module 'vue-router' {
  interface Router {
    i18n?: I18n
  }
}

export const SUPPORT_LOCALES = ['en', 'tr', 'ar', 'ru', 'de', 'fr', 'es', 'uz']
const mapLangCode: Record<string, string> = {
  en: 'en',
  'en-us': 'en',
  tr: 'tr',
  'tr-tr': 'tr',
  ar: 'ar',
  'ar-sa': 'ar',
  ru: 'ru',
  'ru-ru': 'ru',
  de: 'de',
  'de-de': 'de',
  fr: 'fr',
  'fr-fr': 'fr',
  es: 'es',
  'es-es': 'es',
  uz: 'uz',
  'uz-uz': 'uz'
}
/** Crowdin 产出的文件名与内部 locale 不一致时在此映射，保持文件名不变 */
const localeFileMap: Record<string, string> = {
  es: 'es-ES'
}
const rtlMap: Record<string, string> = {
  ar: 'rtl'
}

const getLocaleFileName = (locale: string) => localeFileMap[locale] || locale

// 语言码优先级：传入的参数-->url参数-->navigatorLanguage
export function getFallbackLang(locale: string | null) {
  const urlParams = new URL(window.location.href).searchParams

  locale = locale || urlParams.get('lang')
  locale = locale || navigator.language
  // 根据产品需要，子语言映射到大语言
  locale = mapLangCode[locale.toLowerCase()]
  return SUPPORT_LOCALES.includes(locale) ? locale : 'en'
}

// 应用内单例
let i18n: null | I18n = null

export function setupI18n(): I18n {
  if (i18n) {
    return i18n
  }
  i18n = createI18n({
    legacy: false,
    fallbackLocale: false,
    messages: {},
    missing: () =>
      // console.warn(`Translation key "${key}" not found for locale "${locale}"`)
      '' // 返回空字符串
  })
  // 加载全局多语言
  return i18n as I18n
}

export function $t(k: string) {
  return function () {
    const t = (i18n?.global as unknown as Composer).t // 双重断言
    const message = t(k) // 现在可以调用

    return message
  }
}

const routeLangMap = new Map()

async function getRouteLangFromLocaleFile(prefixKey: string) {
  const m = prefixKey.match(/^(.*)-([a-z]{2})$/i)

  if (!m) {
    return null
  }
  const locale = m[2].toLowerCase()
  const cacheKey = `__route_lang_by_locale__${locale}`
  let grouped = routeLangMap.get(cacheKey)

  if (!grouped) {
    // 保持 Crowdin 文件名：西班牙语为 es-ES.json，其余为短码
    const fileName = getLocaleFileName(locale)

    grouped = await import(`./route-lang/${fileName}.json`)
      .then(messages => {
        routeLangMap.set(cacheKey, messages.default)
        return messages.default
      })
      .catch(() => null)
  }
  if (!grouped) {
    return null
  }
  return grouped
}
async function dynamicImportLang(prefixKey: string) {
  if (routeLangMap.has(prefixKey)) {
    return routeLangMap.get(prefixKey)
  }
  let p = null

  if (prefixKey.startsWith('global-')) {
    p = import(`./global/${prefixKey}.json`)
  } else {
    const fromLocaleFile = await getRouteLangFromLocaleFile(prefixKey)

    if (fromLocaleFile) {
      routeLangMap.set(prefixKey, fromLocaleFile)
      return fromLocaleFile
    }
    p = import(`./route-lang/${prefixKey}.json`)
  }

  return p
    .then(messages => {
      routeLangMap.set(prefixKey, messages.default)
      return messages.default
    })
    .catch(() => {
      console.error('no route i18n config')
      return null
    })
}
function setDomAttr(ele: HTMLElement, attrName: string, val: string) {
  ele.setAttribute(attrName, val)
}
export async function loadLocaleMessages(
  i18n: I18n,
  options: {
    locale?: string
    prefix?: string
  } = {}
) {
  // eslint-disable-next-line prefer-const
  let { locale = '', prefix = '' } = options

  locale = getFallbackLang(locale)
  const globalLangUrl = `global-${locale}`
  const routeLangUrl = `${prefix}-${locale}`
  const [globalLang, routeLang] = await Promise.all([
    dynamicImportLang(globalLangUrl),
    dynamicImportLang(routeLangUrl)
  ])

  // 始终先设置全局再设置路由的
  if (globalLang) {
    i18n.global.mergeLocaleMessage(locale, globalLang)
  }
  if (routeLang) {
    i18n.global.mergeLocaleMessage(locale, routeLang)
  }
  ;(i18n.global.locale as Ref<string>).value = locale
  setDomAttr(document.querySelector('html')!, 'lang', locale)
  const rtlVal = rtlMap[locale] || 'ltr'

  setDomAttr(document.querySelector('html')!, 'dir', rtlVal)
  return nextTick().then(() => rtlVal)
}
export default {
  install(
    app: App,
    options: {
      router: Router
    }
  ) {
    const i18n = setupI18n()

    options.router.i18n = i18n
    app.use(i18n)
    // 路由守卫
    options.router.beforeEach(async function (to, from, next) {
      // 加载该路由的多语言文件
      loadLocaleMessages(i18n, {
        prefix: to.path.replace(/^\//, '').split('/').join('-'),
        locale: (to.query.lang ?? '') as string
      })
      next()
    })
  }
}
